import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DATE_FORMATS, MAT_NATIVE_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { ManagementAiPriceControllerService, ManagementAiPriceRequest, ManagementAiPriceResponse } from 'api-ai-hub';
import { SharedService } from '../../../services/shared.service';

interface DialogData {
	price?: ManagementAiPriceResponse;
}

@Component({
	selector: 'app-ai-price-dialog',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatDialogModule,
		MatButtonModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatIconModule,
		MatDividerModule,
		MatDatepickerModule,
		MatNativeDateModule,
		TranslateModule,
	],
	providers: [
		{ provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS }
	],
	templateUrl: './ai-price-dialog.component.html',
	styleUrl: './ai-price-dialog.component.scss',
})
export class AiPriceDialogComponent {
	form: FormGroup;
	saving = false;
	isEdit = false;
	providerOptions = Object.values(ManagementAiPriceRequest.ProviderEnum) as ManagementAiPriceRequest.ProviderEnum[];

	constructor(
		private fb: FormBuilder,
		private dialogRef: MatDialogRef<AiPriceDialogComponent>,
		private apiAiPrice: ManagementAiPriceControllerService,
		private sharedService: SharedService,
		private translate: TranslateService,
		@Inject(MAT_DIALOG_DATA) public data: DialogData
	) {
		this.isEdit = !!data.price;

		this.form = this.fb.group({
			provider: [data.price?.provider ?? null, [Validators.required]],
			model: [data.price?.model ?? '', [Validators.required]],
			pricePerMillionInputTokens: [data.price?.pricePerMillionInputTokens ?? null, [Validators.required, Validators.min(0)]],
			pricePerMillionOutputTokens: [data.price?.pricePerMillionOutputTokens ?? null, [Validators.required, Validators.min(0)]],
			pricePerMillionCachedInputTokens: [data.price?.pricePerMillionCachedInputTokens ?? null, [Validators.min(0)]],
			effectiveFrom: [data.price?.effectiveFrom ? new Date(data.price.effectiveFrom) : new Date(), [Validators.required]],
		});
	}

	save(): void {
		if (this.form.invalid || this.saving) return;
		this.saving = true;

		const body: ManagementAiPriceRequest = {
			provider: this.form.value.provider,
			model: this.form.value.model.trim(),
			pricePerMillionInputTokens: this.form.value.pricePerMillionInputTokens,
			pricePerMillionOutputTokens: this.form.value.pricePerMillionOutputTokens,
			pricePerMillionCachedInputTokens: this.form.value.pricePerMillionCachedInputTokens ?? undefined,
			effectiveFrom: this.toLocalDateString(this.form.value.effectiveFrom) as unknown as Date,
		};

		const request$ = this.isEdit && this.data.price?.id
			? this.apiAiPrice.updateAiPrice(body, this.data.price.id)
			: this.apiAiPrice.createAiPrice(body);

		request$.pipe(take(1)).subscribe({
			next: response => {
				this.sharedService.showNotification.next({
					message: this.translate.instant(this.isEdit ? 'NOTIFICATIONS.RECORD_UPDATED' : 'NOTIFICATIONS.RECORD_CREATED'),
					type: 'SUCCESS',
				});
				this.dialogRef.close(response);
			},
			error: err => {
				this.saving = false;
				this.sharedService.showNotification.next({
					message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err?.error?.message ?? ''}`,
					type: 'ERROR',
				});
			},
		});
	}

	cancel(): void {
		this.dialogRef.close();
	}

	private toLocalDateString(date: Date): string {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}
}
